import { Injectable } from '@angular/core';
import { EmailTypeEnum, LandingTypeEnum, Screen, ScreenTypeEnum } from 'src/app/common/onboarding/public-api';
import { BehaviorSubject, Observable } from 'rxjs';
import { Variant } from 'src/app/common/models/variant';
import { assocEnumToArray } from 'src/app/ui/assoc-enum-to-array.function';

import { StepSection } from './step-section';
import { StepState } from './step.state';

@Injectable({
  providedIn: 'root',
})
export class StepService {

  private readonly INITIAL_STATE = new StepState(1, 0, false, false, false, [], true, false, false);

  private state = new BehaviorSubject<StepState>(this.INITIAL_STATE);

  public setAllSteps(allSteps: number, isOnboarding: boolean): void {
    const oldState = this.state.getValue();
    this.state.next({ ...oldState, allSteps, isOnboarding });
  }

  public setStep(step: number): void {
    const oldState = this.state.getValue();
    this.state.next({ ...oldState, step });
  }

  public nextStep(): void {
    const oldState = this.state.getValue();
    const step = oldState.step + 1;
    this.state.next({ ...oldState, step });
  }

  public prevStep(): void {
    const oldState = this.state.getValue();
    const step = oldState.step - 1;
    this.state.next({ ...oldState, step });
  }

  public changeStepHidden(stepHidden: boolean): void {
    const oldState = this.state.getValue();
    this.state.next({ ...oldState, stepHidden });
  }

  public changeWithNumbering(withNumbering: boolean): void {
    const oldState = this.state.getValue();
    this.state.next({ ...oldState, withNumbering });
  }

  public getStepState(): Observable<StepState> {
    return this.state.asObservable();
  }

  public next(screen: any): void {
    if (screen.parameters.noCountForStep) {
      return;
    }

    this.nextStep();
  }

  public prev(screen: any): void {
    if (screen.parameters.noCountForStep) {
      return;
    }

    this.prevStep();
  }

  public setSettingsForScreen(screen: any): void {
    this.changeStepHidden(!!screen.parameters.stepHidden);
    this.changeWithNumbering(!!screen.parameters.withNumbering);
  }

  public setAllStepsFromOnboarding(screens: {order: number, info: Screen}[]): void {
    if (!screens.length) {
      return;
    }
    let allSteps = 0;
    screens.forEach(screen => {
      if (!screen.info.parameters.noCountForStep) {
        allSteps ++;
      }
    });
    this.setAllSteps(allSteps, true);
    const screen = screens[0].info;
    this.changeStepHidden(!!screen.parameters.stepHidden);
    this.changeWithNumbering(!!screen.parameters.withNumbering);
  }

  public setAllStepsFromPaymentScreens(screens: {info: Screen; order: number}[]): void {
    let allSteps = 0;
    screens!.forEach(screen => {
      if (this.checkScreenType(screen.info.type, LandingTypeEnum) || this.checkScreenType(screen.info.type, EmailTypeEnum)) {
        if (!screen.info.parameters.noCountForStep) {
          allSteps ++;
        }
      }
    });
    this.setAllSteps(allSteps, false);
    this.setStep(0);
    this.changeWithNumbering(false);
  }

  public setSectionsFromVariant(variant: Variant, screens: {order: number, info: Screen}[]): void {
    if (variant.parameters.progressBarSections) {
      const sectionsWithCheckmarks = variant.parameters.sectionsWithCheckmarks;
      const useSeparateSections = variant.parameters.useSeparateSections;
      const onboardingSections: StepSection[] = [];
      // const paymentSections: StepSection[] = [];
      let counter = 0;
      const considerScreens = screens.filter(item => !item.info.parameters.noCountForStep);
      let currentSection = considerScreens[0].info.parameters.section;
      considerScreens.forEach((elem, n) => {
        const section = elem.info.parameters.section;
        if (section === currentSection) {
          counter ++;
        } else {
          onboardingSections.push(new StepSection(currentSection, counter));
          currentSection = section;
          counter = 1;
        }

        if (n === considerScreens.length - 1) {
          onboardingSections.push(new StepSection(currentSection, counter));
        }
      });

      const oldState = this.state.getValue();
      const useSections = true;
      
      this.state.next({ ...oldState, useSections, onboardingSections, sectionsWithCheckmarks, useSeparateSections });
    }
  }

  private checkScreenType(
    type: ScreenTypeEnum | null,
    en: any): boolean {
    return !!assocEnumToArray(en).find(item => item === type);
  }
}
